<template>
    <div class="cover-uploader mt-2 relative rounded-lg border border-dashed border-gray-900/25">
        <div id="cover-preview"
            class="group relative w-full h-[250px] bg-cover bg-center rounded-lg"
            ref="cover-preview"
            @mousemove="handleCoverPosition"
            :style="{
                'background-image': `url('${preview_url ?? (cover_url ?? '')}')`,
                'background-position': `${currentPosition.x}% ${currentPosition.y}%`,
                'height': height ? `${height}px` : '250px',
            }">

            <div id="draggable"
                class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[15px] h-[15px] bg-[#f1f3f9] border-[3px] border-[#f59c25] rounded-full cursor-move z-10"
                ref="draggable"
                @mousedown="startDragging"
                @mouseup="stopDragging"
                :style="{'display': cover_url ? 'block' : 'none'}"
                v-if="!is_file_uploading">
            </div>

            <div class="flex justify-center items-center h-full" v-if="!cover_url && !is_file_uploading">
                <div class="text-center">
                    <PhotoIcon class="mx-auto size-12 text-gray-300" aria-hidden="true" />
                    <div class="flex justify-center text-sm/6 text-gray-600">
                        <label for="cover-uploader-input" class="relative cursor-pointer rounded-md bg-white font-semibold text-blue-700 focus-within:ring-2 focus-within:ring-blue-700 focus-within:ring-offset-2 focus-within:outline-hidden hover:text-blue-600">
                            <span>{{ $t('cover_upload.upload_file') }}</span>
                        </label>
<!--                        <p class="pl-1">or drag and drop</p>-->
                    </div>
                    <p class="text-xs/5 text-gray-600" v-if="allowVideo">{{ $t('cover_upload.upload_tip') }}</p>
                    <p class="text-xs/5 text-gray-600" v-else>{{ $t('cover_upload.upload_limits') }}</p>
                </div>
            </div>

            <div class="absolute bottom-0 left-0 w-full h-[30px] rounded-b-lg bg-gradient-to-b from-transparent to-black/65 text-white font-bold cursor-pointer flex items-center justify-center transition-opacity duration-300 lg:opacity-0 group-hover:opacity-100"
                @click="clickSelectCoverFile"
                v-if="!is_file_uploading && cover_url">
                <span>
                    {{ (!is_file_selected && !cover_url) ? $t('cover_upload.upload') : $t('cover_upload.replace') }}
                </span>
            </div>
        </div>

        <div class="cover-loader absolute top-0 left-0 w-full h-full rounded-lg bg-black/60 text-white font-bold flex items-center justify-center"
            v-if="is_file_uploading">
            <span>{{ $t('cover_upload.uploading') }}</span>
        </div>

        <input type="file"
            name="cover"
            accept="image/*,video/*"
            id="cover-uploader-input"
            @change="selectCoverFile"
            ref="cover-uploader-input"
            class="hidden">
    </div>
</template>

<script lang="ts">
import {defineComponent} from "vue";
import {PhotoIcon} from "@heroicons/vue/24/solid";

export default defineComponent({
    name: "CoverUploaderComponent",

    components: {
        PhotoIcon,
    },

    props: {
        preview_url: String,
        cover_url: String,

        allowVideo: {
            type: Boolean,
            default: false,
        },

        is_file_selected: {
            type: Boolean,
            default: false,
        },

        is_file_uploading: {
            type: Boolean,
            default: false,
        },

        position: {
            type: Object,
            default: () => ({x: 50, y: 50}),
        },

        height: {
            type: Number,
            default: null,
        },
    },

    data() {
        return {
            isDragging: false,

            currentPosition: {x: 50, y: 50}
        };
    },

    emits: ["cover-file-selected", "position-updated", "position-updating"],

    mounted() {
        this.currentPosition = {
            x: this.position.x,
            y: this.position.y,
        }

        this.$nextTick(() => {
            const coverPreview = this.$refs["cover-preview"] as HTMLElement;
            const draggable = this.$refs["draggable"] as HTMLElement;

            const maxX = coverPreview.clientWidth;
            const maxY = coverPreview.clientHeight;

            const x = (this.currentPosition.x / 100) * maxX;
            const y = (this.currentPosition.y / 100) * maxY;

            draggable.style.left = `${x}px`;
            draggable.style.top = `${y}px`;
        });
    },

    methods: {
        handleCoverPosition(event: MouseEvent) {
            if (!this.isDragging) {
                return;
            }

            const coverPreview = this.$refs["cover-preview"] as HTMLElement;
            const draggable = this.$refs["draggable"] as HTMLElement;

            const rect = coverPreview.getBoundingClientRect();
            const offsetX = event.clientX - rect.left;
            const offsetY = event.clientY - rect.top;

            const minX = 0;
            const minY = 0;
            const maxX = coverPreview.clientWidth;
            const maxY = coverPreview.clientHeight;

            const x = Math.max(minX, Math.min(offsetX, maxX));
            const y = Math.max(minY, Math.min(offsetY, maxY));

            this.currentPosition.x = (x / maxX) * 100;
            this.currentPosition.y = (y / maxY) * 100;

            draggable.style.left = `${x}px`;
            draggable.style.top = `${y}px`;

            this.$emit("position-updating", this.currentPosition);
        },

        startDragging() {
            this.isDragging = true;
        },

        stopDragging() {
            this.isDragging = false;

            this.$emit("position-updated", this.currentPosition);
        },

        selectCoverFile(event: Event) {
            const input = event.target as HTMLInputElement;
            const file = input.files?.[0];

            if (file) {
                this.$emit("cover-file-selected", file);
            }
        },

        clickSelectCoverFile() {
            const input = this.$refs["cover-uploader-input"] as HTMLInputElement;
            input.click();
        },
    },
});
</script>

<style lang="scss" scoped>
.cover-loader {
    cursor: progress;
}
</style>